import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import SelectBox from '../../shared/component/SelectBox';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getOnBoardingMessage } from '../../shared/constants/onBoardingMessages';
import { getDefaultOnBoardingData } from '../../shared/converter/entityBuilder';
import { AlertPosition, AlertType, ModalType, SignupArchiveType } from '../../shared/enum/EnumRepository';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useValidation } from '../../shared/hooks/useValidation';
import { useLoadingStore } from '../../shared/store/loadingStore';
import { useOnBoardStore } from '../../shared/store/onBoardStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import MoleculeAuthInput from './molecules/MoleculeAuthInput';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

type ActivityTypeValue = SignupArchiveType | '';

const activityTypeOptions = Object.values(SignupArchiveType);
const activityTypeLabels: Record<SignupArchiveType, string> = {
  [SignupArchiveType.ARTIST]: 'Artist',
  [SignupArchiveType.CURATOR]: 'Curator',
  [SignupArchiveType.RESEARCHER]: 'Researcher',
  [SignupArchiveType.EDUCATOR]: 'Educator',
  [SignupArchiveType.GROUP]: 'Group',
};

const Register: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { checkEmail, checkHandle, signUp } = useAuth();
  const setOnBoardingData = useOnBoardStore((state) => state.setOnBoardingData);
  const updateOnBoardingData = useOnBoardStore((state) => state.updateOnBoardingData);
  const { isLoading } = useLoadingStore();
  const { clearModal, setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const {
    isEmail,
    isValidHandle,
    validateHandle: getHandleError,
    PASSWORD_REGEX,
    explainPasswordIssues,
  } = useValidation();

  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activityType, setActivityType] = useState<SignupArchiveType | null>(null);
  const [handleError, setHandleError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [requiredOver14, setRequiredOver14] = useState(false);
  const [requiredTerms, setRequiredTerms] = useState(false);
  const [requiredPrivacy, setRequiredPrivacy] = useState(false);
  const [optionalPromotion, setOptionalPromotion] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const isAllRequiredAgree = requiredOver14 && requiredTerms && requiredPrivacy;

  useEffect(() => {
    setIsUserInput(Boolean(
      handle ||
      email ||
      password ||
      activityType ||
      requiredOver14 ||
      requiredTerms ||
      requiredPrivacy ||
      optionalPromotion
    ));
  }, [
    handle,
    email,
    password,
    activityType,
    requiredOver14,
    requiredTerms,
    requiredPrivacy,
    optionalPromotion,
    setIsUserInput,
  ]);

  useEffect(() => {
    setOnBoardingData(getDefaultOnBoardingData());
    modalRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [setOnBoardingData]);

  const validateHandleValue = (value: string) => {
    if (value.length === 0) {
      setHandleError('');
      return true;
    }

    const error = getHandleError(value);
    setHandleError(error ?? '');
    return error == null;
  };

  const changeHandleInput = (value: string) => {
    const lowerValue = value.toLowerCase();
    validateHandleValue(lowerValue);
    setHandle(lowerValue);
  };

  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError('');
      return true;
    }
    if (!isEmail(value)) {
      setEmailError(getOnBoardingMessage('VALIDATION_EMAIL_INVALID') as string);
      return false;
    }
    setEmailError('');
    return true;
  };

  const changeEmailInput = (value: string) => {
    validateEmail(value);
    setEmail(value);
  };

  const validatePassword = (value: string) => {
    if (value.length === 0) {
      setPasswordError('');
      return true;
    }
    if (!PASSWORD_REGEX.test(value)) {
      setPasswordError(explainPasswordIssues(value)[0] ?? getAlertMessage('CHECK_FORMAT'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const changePasswordInput = (value: string) => {
    validatePassword(value);
    setPassword(value);
  };

  const getActivityTypeLabel = (value: ActivityTypeValue) => {
    if (!value) return getOnBoardingMessage('STEP_TITLE_ACTIVITY_TYPE') as string;
    return activityTypeLabels[value];
  };

  const handleSubmit = async () => {
    if (isLoading > 0) return;

    if (!handle || !email || !password || !activityType) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }

    const valid = [
      validateHandleValue(handle),
      validateEmail(email),
      validatePassword(password),
    ].every(Boolean);

    if (!valid || !isValidHandle(handle)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT'),
      });
      return;
    }

    if (!isAllRequiredAgree) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }

    if (!useOnBoardStore.getState().onBoardingData) {
      setOnBoardingData(getDefaultOnBoardingData());
    }
    updateOnBoardingData({
      email,
      handle,
      signupArchiveType: activityType,
      verifiedEmail: false,
      verifiedHandle: false,
      works: [],
      isPreviewGuideMode: false,
      isAgreePolicy: true,
    });
    await checkEmail(email);
    if (!useOnBoardStore.getState().onBoardingData?.verifiedEmail) return;

    updateOnBoardingData({ verifiedHandle: false });
    await checkHandle(handle);
    if (!useOnBoardStore.getState().onBoardingData?.verifiedHandle) return;

    await signUp({
      email,
      password,
      handle,
      over14: requiredOver14,
      agreeTos: requiredTerms,
      agreePrivacy: requiredPrivacy,
      marketingEmailOptIn: optionalPromotion,
      signupArchiveType: activityType,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading > 0) return;
    if (event.key === 'Enter' && event.target instanceof HTMLButtonElement) return;
    if (event.key === 'Enter') handleSubmit();
    if (event.key === 'Escape') clearModal();
  };

  const openLoginModal = () => {
    setStandardModal({
      modalType: ModalType.LOGIN,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  const openPolicy = (event: React.MouseEvent<HTMLSpanElement>, url: string) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <RegisterComp ref={modalRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <HeaderRow>
        <Title>{getBtnName('REGISTER')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
        />
      </HeaderRow>

      <FieldsContainer>
        <FieldURLGroup>
          <MoleculeAuthInput
            name={getOnBoardingMessage('STEP_TITLE_PROFILE_URL') as string}
            type="text"
            inputName="handle"
            autoComplete="nickname"
            value={handle}
            handleChange={changeHandleInput}
            err={handleError || null}
            placeholder="url_name"
          />
          {!handleError && (
            <HandlePreview>
              www.architrave.work/@<HighlightText>{handle}</HighlightText>
            </HandlePreview>
          )}
        </FieldURLGroup>

        <FieldGroup>
          <MoleculeAuthInput
            name={getModalMessage('AUTH_EMAIL_LABEL')}
            type="email"
            inputName="email"
            autoComplete="email"
            value={email}
            handleChange={changeEmailInput}
            err={emailError || null}
            placeholder="username@email.com"
          />
        </FieldGroup>

        <FieldGroup>
          <MoleculeAuthInput
            name={getModalMessage('AUTH_PASSWORD_LABEL')}
            inputName="password"
            autoComplete="new-password"
            value={password}
            handleChange={changePasswordInput}
            err={passwordError || null}
            placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
          />
        </FieldGroup>

        <FieldActivityGroup>
          <FieldLabel>{getOnBoardingMessage('STEP_TITLE_ACTIVITY_TYPE')}</FieldLabel>
          <ActivitySelectWrapper>
            <SelectBox<ActivityTypeValue>
              value={activityType ?? ''}
              options={activityTypeOptions}
              handleChange={(value) => {
                if (value) setActivityType(value);
              }}
              direction={false}
              getDisplayText={getActivityTypeLabel}
              getOptionDisplayText={getActivityTypeLabel}
            />
          </ActivitySelectWrapper>
        </FieldActivityGroup>
      </FieldsContainer>

      <ConsentContainer>
        <CheckboxComp>
          <UniversalCheckbox
            checked={requiredOver14}
            onChange={() => setRequiredOver14((checked) => !checked)}
          />
          <span>{getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_AGE')}</span>
        </CheckboxComp>
        <CheckboxComp>
          <UniversalCheckbox
            checked={requiredTerms}
            onChange={() => setRequiredTerms((checked) => !checked)}
          />
          <span>
            {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_TERMS_BEFORE')}
            <NavLink onClick={(event) => openPolicy(event, '/policy/terms')}>
              {getModalMessage('CONSENT_TERMS_LINK')}
            </NavLink>
            {getModalMessage('CONSENT_TERMS_AFTER')}
          </span>
        </CheckboxComp>
        <CheckboxComp>
          <UniversalCheckbox
            checked={requiredPrivacy}
            onChange={() => setRequiredPrivacy((checked) => !checked)}
          />
          <span>
            {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_PRIVACY_BEFORE')}
            <NavLink onClick={(event) => openPolicy(event, '/policy/privacy')}>
              {getModalMessage('CONSENT_PRIVACY_LINK')}
            </NavLink>
            {getModalMessage('CONSENT_PRIVACY_AFTER')}
          </span>
        </CheckboxComp>
        <CheckboxComp>
          <UniversalCheckbox
            checked={optionalPromotion}
            onChange={() => setOptionalPromotion((checked) => !checked)}
          />
          <span>{getModalMessage('CONSENT_OPTIONAL_LABEL')} {getModalMessage('CONSENT_PROMO')}</span>
        </CheckboxComp>
      </ConsentContainer>

      <SubmitButton onClick={handleSubmit} disabled={isLoading > 0}>
        {getBtnName('REGISTER')}
      </SubmitButton>

      <SubContainer>
        <ToggleText>
          <span>{getModalMessage('REGISTER_HAVE_ACCOUNT')}</span>
          <LinkText onClick={openLoginModal}>{getBtnName('LOGIN')}</LinkText>
        </ToggleText>
      </SubContainer>
    </RegisterComp>
  );
};

const RegisterComp = styled.div`
  display: flex;
  max-height: 86dvh;
  flex-direction: column;
  padding: 17px 14px;
  overflow-y: auto;
  outline: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 44px;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const FieldURLGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 80px;
  margin-bottom: 8px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 84px;
`;

const FieldActivityGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 50px;
`;
const HandlePreview = styled.p`
  margin-top: -2px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const HighlightText = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;

const FieldLabel = styled.h3`
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const ActivitySelectWrapper = styled.div`
  width: clamp(100px, 8vw, 120px);
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ConsentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 10px;
`;

const CheckboxComp = styled.div`
  display: flex;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};

  input {
    flex: 0 0 12px;
    margin-top: 2px;
  }
`;

const NavLink = styled.span`
  margin: 0 3px;
  text-decoration: underline;
  cursor: pointer;
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 10px 0px 20px 0;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

const ToggleText = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const LinkText = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-style: italic;
  text-decoration: underline;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

export default Register;
